// src/context/CartContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);

    // Recalcular total cuando cambian los items
    useEffect(() => {
        const newTotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        setTotal(newTotal);
    }, [items]);

    const addItem = (product, quantity = 1) => {
        setItems(prevItems => {
            // Si el producto tiene opciones personalizadas, siempre añadirlo como un nuevo item
            if (product.hasOptions) {
                // Generar un ID único para el producto personalizado
                const customProductId = `${product.id}-custom-${Date.now()}`;
                return [...prevItems, { ...product, id: customProductId, quantity: 1 }];
            }

            // Para productos normales, verificar si ya existe
            const existingItemIndex = prevItems.findIndex(item => item.id === product.id);

            if (existingItemIndex !== -1) {
                // Si ya existe, incrementar la cantidad
                const updatedItems = [...prevItems];
                updatedItems[existingItemIndex] = {
                    ...updatedItems[existingItemIndex],
                    quantity: updatedItems[existingItemIndex].quantity + quantity
                };
                return updatedItems;
            } else {
                // Si no existe, agregar nuevo item
                return [...prevItems, { ...product, quantity }];
            }
        });
    };

    const removeItem = (productId) => {
        setItems(prevItems => prevItems.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, quantity) => {
        if (quantity <= 0) {
            removeItem(productId);
            return;
        }

        setItems(prevItems =>
            prevItems.map(item =>
                item.id === productId
                    ? { ...item, quantity }
                    : item
            )
        );
    };

    const clearCart = () => {
        setItems([]);
    };

    return (
        <CartContext.Provider value={{
            items,
            total,
            addItem,
            removeItem,
            updateQuantity,
            clearCart,
            itemCount: items.reduce((count, item) => count + item.quantity, 0)
        }}>
            {children}
        </CartContext.Provider>
    );
};

export default CartContext;